/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import Icon from 'src/components/Icon'
import { captionXSmall } from 'src/components/css'

/**
 * SN-4709 Team Privacy (Inon, Sep 2) — the PRIVATE state, given a form.
 *
 * The invite card exists because of a state ("this team is hidden"), and until
 * now nothing but two paragraphs of prose carried it: side by side, the private
 * and public variants of the card were distinguishable only by reading them. The
 * lock is what makes the state readable at a glance, so the title no longer has
 * to carry it as an em-dashed suffix.
 *
 * NEUTRAL, not the tenant accent, for two reasons. The accent belongs to the one
 * thing on this card you are meant to DO — copy the link — and a second amber
 * object beside it splits that. And an accent-tinted pill does not survive both
 * tenants: `colors.PRIMARY` is `#004FFF` on Canlan, which as text on the card's
 * `NEUTRAL_700` ground lands around 2.5:1. The lock carries the meaning; the
 * colour does not have to.
 *
 * ALL CAPS by design — a status label, which the Title Case rule exempts.
 */
const PrivateTeamBadge = () => (
  <span css={badgeStyle} data-testid='private-team-badge'>
    <Icon faType='fas' name='lock' fontSize={9} color={colors.NEUTRAL_50} />
    PRIVATE
  </span>
)

export default PrivateTeamBadge

// ---- styled ----

/**
 * `captionXSmall` already is uppercase, 600 and tracked out — this is a caption
 * in the exact sense the preset was written for, so nothing here re-declares a
 * size or a weight.
 */
const badgeStyle = css`
  ${captionXSmall}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 8px;
  background: ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_30};
  white-space: nowrap;
`
